import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { X, AlertCircle } from "lucide-react";

export default function EditStudentForm({ student, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    student_id: student.student_id || "",
    first_name: student.first_name || "",
    last_name: student.last_name || "",
    grade_level: student.grade_level || "",
    email: student.email || "",
    f1g1_parent_phone: student.f1g1_parent_phone || "",
    f1g2_parent_phone: student.f1g2_parent_phone || "",
    f1g1_parent_email: student.f1g1_parent_email || "",
    f2g1_parent_phone: student.f2g1_parent_phone || "",
    f2g1_parent_email: student.f2g1_parent_email || "",
    driveline_tag: student.driveline_tag || "",
    barcode: student.barcode || ""
  });
  const [error, setError] = useState(null);

  const queryClient = useQueryClient();

  const updateStudentMutation = useMutation({
    mutationFn: async (data) => {
      return base44.entities.Student.update(student.id, {
        student_id: data.student_id,
        first_name: data.first_name,
        last_name: data.last_name,
        grade_level: data.grade_level || undefined,
        email: data.email || undefined,
        f1g1_parent_phone: data.f1g1_parent_phone || undefined,
        f1g2_parent_phone: data.f1g2_parent_phone || undefined,
        f1g1_parent_email: data.f1g1_parent_email || undefined,
        f2g1_parent_phone: data.f2g1_parent_phone || undefined,
        f2g1_parent_email: data.f2g1_parent_email || undefined,
        driveline_tag: data.driveline_tag || undefined,
        barcode: data.barcode || undefined
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
      onSuccess();
    },
    onError: (err) => {
      setError(err.message || "Failed to update student");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.student_id.trim()) {
      setError("Student ID is required");
      return;
    }
    
    if (!formData.first_name.trim()) {
      setError("First name is required");
      return;
    }
    
    if (!formData.last_name.trim()) {
      setError("Last name is required");
      return;
    }
    
    updateStudentMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle>Edit Student</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student_id">Student ID *</Label>
                <Input
                  id="student_id"
                  value={formData.student_id}
                  onChange={(e) => setFormData({...formData, student_id: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="grade_level">Grade Level</Label>
                <Input
                  id="grade_level"
                  value={formData.grade_level}
                  onChange={(e) => setFormData({...formData, grade_level: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Student Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="driveline_tag">Driveline Tag</Label>
                <Input
                  id="driveline_tag"
                  value={formData.driveline_tag}
                  onChange={(e) => setFormData({...formData, driveline_tag: e.target.value})}
                  placeholder="Car rider tag number"
                />
              </div>
              <div>
                <Label htmlFor="barcode">Barcode</Label>
                <Input
                  id="barcode"
                  value={formData.barcode}
                  onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                  placeholder="QR/Barcode for attendance"
                />
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="font-semibold text-slate-900 mb-3">Parent Contact Information</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="f1g1_parent_phone">F1G1 Parent Phone</Label>
                    <Input
                      id="f1g1_parent_phone"
                      value={formData.f1g1_parent_phone}
                      onChange={(e) => setFormData({...formData, f1g1_parent_phone: e.target.value})}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="f1g1_parent_email">F1G1 Parent Email</Label>
                    <Input
                      id="f1g1_parent_email"
                      type="email"
                      value={formData.f1g1_parent_email}
                      onChange={(e) => setFormData({...formData, f1g1_parent_email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="f1g2_parent_phone">F1G2 Parent Phone #2</Label>
                    <Input
                      id="f1g2_parent_phone"
                      value={formData.f1g2_parent_phone}
                      onChange={(e) => setFormData({...formData, f1g2_parent_phone: e.target.value})}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="f2g1_parent_phone">F2G1 Parent Phone</Label>
                    <Input
                      id="f2g1_parent_phone"
                      value={formData.f2g1_parent_phone}
                      onChange={(e) => setFormData({...formData, f2g1_parent_phone: e.target.value})}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="f2g1_parent_email">F2G1 Parent Email</Label>
                  <Input
                    id="f2g1_parent_email"
                    type="email"
                    value={formData.f2g1_parent_email}
                    onChange={(e) => setFormData({...formData, f2g1_parent_email: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={updateStudentMutation.isPending}>
              {updateStudentMutation.isPending ? "Updating..." : "Update Student"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}